/**
 * ExecutionTimelineSummary — Linha do tempo executiva vertical.
 */
import React from 'react';
import { Circle } from 'lucide-react';

export interface TimelineSummaryEvent {
  id: string;
  dateLabel: string;
  title: string;
  detail?: string;
  tone?: 'default' | 'ok' | 'warning' | 'danger' | 'info';
}

interface ExecutionTimelineSummaryProps {
  events: TimelineSummaryEvent[];
  emptyFallback?: React.ReactNode;
  compact?: boolean;
}

const DOT: Record<NonNullable<TimelineSummaryEvent['tone']>, string> = {
  default: 'border-slate-300 bg-white dark:border-slate-600',
  ok: 'border-emerald-500 bg-emerald-50',
  warning: 'border-amber-500 bg-amber-50',
  danger: 'border-rose-500 bg-rose-50',
  info: 'border-blue-500 bg-blue-50',
};

export const ExecutionTimelineSummary: React.FC<ExecutionTimelineSummaryProps> = ({
  events,
  emptyFallback,
  compact = false,
}) => {
  if (events.length === 0) {
    return (
      <div className="px-3 py-2">
        {emptyFallback ?? (
          <div className="rounded-sm border border-dashed border-slate-200 bg-slate-50/80 px-3 py-3 dark:border-slate-700 dark:bg-slate-900/30">
            <p className="text-[10px] font-semibold text-slate-600 dark:text-slate-300">Linha do tempo em formação</p>
            <p className="mt-0.5 text-[9px] text-slate-500">
              A solicitação foi aberta. Movimentações aparecerão aqui conforme a execução avança.
            </p>
          </div>
        )}
      </div>
    );
  }

  return (
    <ol className={`relative ${compact ? 'px-2 py-1' : 'px-3 py-2'}`}>
      <div className="absolute bottom-2 left-[18px] top-2 w-px bg-slate-200 dark:bg-slate-700" aria-hidden />
      {events.map((ev, i) => (
        <li key={ev.id} className="relative flex gap-2.5 pb-3 last:pb-0">
          <span
            className={`relative z-[1] mt-0.5 flex h-[10px] w-[10px] shrink-0 items-center justify-center rounded-full border-2 ${DOT[ev.tone ?? 'default']}`}
          >
            {i === 0 && <Circle size={4} className="text-slate-400" fill="currentColor" />}
          </span>
          <div className="min-w-0 flex-1">
            <p className="font-mono text-[9px] font-bold uppercase tracking-wide text-slate-400">{ev.dateLabel}</p>
            <p className="text-[11px] font-semibold text-slate-800 dark:text-slate-100">{ev.title}</p>
            {ev.detail && (
              <p className="mt-0.5 text-[9px] leading-relaxed text-slate-500 dark:text-slate-400 line-clamp-2">
                {ev.detail}
              </p>
            )}
          </div>
        </li>
      ))}
    </ol>
  );
};

export default ExecutionTimelineSummary;
